import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeSwitcher from './ThemeSwitcher';
import ChangePasswordModal from './ChangePasswordModal';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const canAccessSupport = Boolean(user);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className={`layout ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
      {/* Hamburger - mobile only */}
      <button
        className="hamburger-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? '✕' : '☰'}
      </button>

      {/* Overlay - mobile only */}
      <div
        className={`mobile-overlay ${mobileMenuOpen ? 'visible' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <nav className={`sidebar ${sidebarOpen ? '' : 'collapsed'} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          {sidebarOpen && <h1>Winalco C&L</h1>}
          <button className="toggle-btn" onClick={toggleSidebar}>
            {sidebarOpen ? '«' : '»'}
          </button>
        </div>

        <ul className="nav-links">
          {hasPermission('clients', 'view') && (
            <li>
              <Link to="/clients" className={isActive('/clients') ? 'active' : ''} title="Clients" onClick={handleNavClick}>
                <span className="nav-icon">🏢</span>
                {sidebarOpen && <span className="nav-text">Clients</span>}
              </Link>
            </li>
          )}
          {hasPermission('contrats', 'view') && (
            <li>
              <Link to="/" className={isActive('/') ? 'active' : ''} title="Contrats" onClick={handleNavClick}>
                <span className="nav-icon">📄</span>
                {sidebarOpen && <span className="nav-text">Contrats</span>}
              </Link>
            </li>
          )}
          {hasPermission('licenses', 'view') && (
            <li>
              <Link to="/licenses" className={isActive('/licenses') ? 'active' : ''} title="Licences" onClick={handleNavClick}>
                <span className="nav-icon">🔑</span>
                {sidebarOpen && <span className="nav-text">Licences</span>}
              </Link>
            </li>
          )}
          {hasPermission('licenses', 'view') && (
            <li>
              <Link to="/cnrc-desktop-licenses" className={isActive('/cnrc-desktop-licenses') ? 'active' : ''} title="CNRC Desktop" onClick={handleNavClick}>
                <span className="nav-icon">💻</span>
                {sidebarOpen && <span className="nav-text">CNRC Desktop</span>}
              </Link>
            </li>
          )}
          {hasPermission('desktop_versions', 'view') && (
            <li>
              <Link to="/versions" className={isActive('/versions') ? 'active' : ''} title="Gestion des versions" onClick={handleNavClick}>
                <span className="nav-icon">📦</span>
                {sidebarOpen && <span className="nav-text">Versions</span>}
              </Link>
            </li>
          )}
          {hasPermission('ddns', 'view') && (
            <li>
              <Link to="/acces-distant" className={isActive('/acces-distant') ? 'active' : ''} title="Accès distant" onClick={handleNavClick}>
                <span className="nav-icon">🌐</span>
                {sidebarOpen && <span className="nav-text">Accès distant</span>}
              </Link>
            </li>
          )}
          {hasPermission('creances', 'view') && (
            <li>
              <Link to="/creances" className={isActive('/creances') ? 'active' : ''} title="Suivi des créances" onClick={handleNavClick}>
                <span className="nav-icon">💰</span>
                {sidebarOpen && <span className="nav-text">Créances</span>}
              </Link>
            </li>
          )}
          {hasPermission('contrats', 'view') && (
            <li>
              <Link to="/expired" className={isActive('/expired') ? 'active' : ''} title="Contrats expirés" onClick={handleNavClick}>
                <span className="nav-icon">⚠️</span>
                {sidebarOpen && <span className="nav-text">Contrats expirés</span>}
              </Link>
            </li>
          )}
          {canAccessSupport && (
            <li>
              <Link to="/support" className={isActive('/support') ? 'active' : ''} title="Support" onClick={handleNavClick}>
                <span className="nav-icon">🎫</span>
                {sidebarOpen && <span className="nav-text">Tickets</span>}
              </Link>
            </li>
          )}
          {canAccessSupport && (
            <li>
              <Link to="/support-chat" className={isActive('/support-chat') ? 'active' : ''} title="Chat support" onClick={handleNavClick}>
                <span className="nav-icon">💬</span>
                {sidebarOpen && <span className="nav-text">Chat</span>}
              </Link>
            </li>
          )}
          {hasPermission('admin', 'access') && (
            <li>
              <Link to="/admin" className={isActive('/admin') ? 'active' : ''} title="Administration" onClick={handleNavClick}>
                <span className="nav-icon">⚙️</span>
                {sidebarOpen && <span className="nav-text">Administration</span>}
              </Link>
            </li>
          )}
        </ul>

        <div className="sidebar-footer">
          <ThemeSwitcher sidebarOpen={sidebarOpen} />
          <div className="user-info">
            <span className="user-icon">🧑‍💻</span>
            {sidebarOpen && (
              <span className="user-name">
                {user?.user_name}
                {user?.role && <small style={{ display: 'block', opacity: 0.7, fontSize: '0.75em' }}>{user.role}</small>}
              </span>
            )}
          </div>
          <button className="logout-btn" onClick={() => setShowChangePassword(true)} title="Changer le mot de passe">
            <span className="nav-icon">🔒</span>
            {sidebarOpen && <span className="nav-text">Changer mot de passe</span>}
          </button>
          <button className="logout-btn" onClick={handleLogout} title="Déconnexion">
            <span className="nav-icon">⏻</span>
            {sidebarOpen && <span className="nav-text">Déconnexion</span>}
          </button>
        </div>
      </nav>

      <main className="main-content">
        <Outlet />
      </main>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default Layout;
